import { Temporal } from 'temporal-polyfill';
import { DateTimeZone } from '../types/date-timezone.enum';

type DateLike = Date | string | Temporal.PlainDate;

/**
 * It converts a Date object or date string to a Temporal.PlainDate.
 * When a date string with time component is provided, it converts it to PlainDate in the system's time zone.
 * @param {Date | string} date - The date to be converted.
 * @returns {Temporal.PlainDate}
 */
export function toPlainDate(date: DateLike): Temporal.PlainDate {
  if (date instanceof Temporal.PlainDate) {
    return date;
  } else if (date instanceof Date) {
    return toInstant(date).toZonedDateTimeISO(DateTimeZone.UTC).toPlainDate();
  } else if (date.includes('T')) {
    // ISO string with timezone
    return toInstant(date).toZonedDateTimeISO(DateTimeZone.UTC).toPlainDate();
  } else {
    // Plain date string
    return Temporal.PlainDate.from(date);
  }
}

/**
 * It converts a Date object or ISO timestamp string to a Temporal.Instant.
 * @param {Date} date - The date to be converted.
 * @returns {Temporal.Instant}
 */
export function toInstant(date: DateLike): Temporal.Instant {
  if (date instanceof Date) {
    return Temporal.Instant.fromEpochMilliseconds(date.getTime());
  } else if (date instanceof Temporal.PlainDate) {
    const zonedDateTime = date.toZonedDateTime(DateTimeZone.UTC);

    return zonedDateTime.toInstant();
  } else {
    return Temporal.Instant.from(date);
  }
}

/**
 * Converts a Temporal.Instant to a Temporal.ZonedDateTime in the specified time zone.
 * @param {Temporal.Instant} date
 * @param {DateTimeZone} timeZone
 * @returns
 */
export function toZonedDateTime(date: Temporal.Instant, timeZone: DateTimeZone): Temporal.ZonedDateTime {
  return date.toZonedDateTimeISO(timeZone);
}

/**
 * Converts a Temporal.PlainDate to a JavaScript Date object.
 * @param date
 * @returns
 */
export function toJsDate(date: Temporal.PlainDate): Date {
  return new Date(toISOString(date));
}

/**
 * Get the current instant (for precise timezone operations)
 * @returns Current moment as Temporal.Instant
 */
export function nowInstant(): Temporal.Instant {
  return Temporal.Now.instant();
}

/**
 * Get the current date as a Temporal.PlainDate
 * @returns Today's date in ISO calendar
 */
export function today(): Temporal.PlainDate {
  return Temporal.Now.plainDateISO();
}

/**
 * Returns the locale date format pattern using `Intl.DateTimeFormat`.
 * For example, for 'en-US' it returns 'mm/dd/yyyy', and for 'de-CH' it returns 'dd.mm.yyyy'.
 * @param {string} locale - The locale identifier (e.g., 'en-US', 'de-CH').
 * @returns {string} The date format pattern for the specified locale.
 * @note This function assumes that the date format uses year, month, and day components. It may not cover all edge cases or calendar systems.
 */
export function getLocaleDateFormat(locale: string): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date());

  return parts
    .map((part) => {
      switch (part.type) {
        case 'year':
          return 'yyyy';
        case 'month':
          return 'mm';
        case 'day':
          return 'dd';
        case 'literal':
          return part.value;
        default:
          return '';
      }
    })
    .join('');
}

/**
 * Compare two dates
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
function compareDates(date1: DateLike, date2: DateLike): number {
  const plainDate1 = date1 instanceof Temporal.PlainDate ? date1 : toPlainDate(date1);
  const plainDate2 = date2 instanceof Temporal.PlainDate ? date2 : toPlainDate(date2);

  return Temporal.PlainDate.compare(plainDate1, plainDate2);
}

/**
 * Checks if the first date is before the second date. (compares year-month-day only, ignores time)
 * @param {DateLike} date1 - The first date to compare.
 * @param {DateLike} date2 - The second date to compare.
 * @param {Temporal.DurationLike} offset - Optional duration offset to subtract from date2 (e.g., { days: 1 })
 * @returns {boolean} - True if date1 is before date2 (+ offset).
 */
export function isDateBefore(date1: DateLike, date2: DateLike, offset?: Temporal.DurationLike): boolean {
  const date2WithOffset = offset ? toPlainDate(date2).add(offset) : date2;

  return compareDates(date1, date2WithOffset) < 0;
}

/**
 * Checks if the first date is after the second date. (compares year-month-day only, ignores time)
 * @param {DateLike} date1 - The first date to compare.
 * @param {DateLike} date2 - The second date to compare.
 * @param {Temporal.DurationLike} offset - Optional offset to add to date2 before comparison.
 * @returns {boolean} - True if date1 is after date2 (+ offset).
 */
export function isDateAfter(date1: DateLike, date2: DateLike, offset?: Temporal.DurationLike): boolean {
  const date2WithOffset = offset ? toPlainDate(date2).add(offset) : date2;

  return compareDates(date1, date2WithOffset) > 0;
}

/**
 * Checks if the first date is before or equal to the second date. (compares year-month-day only, ignores time)
 * @param {DateLike} date1 - The first date to compare.
 * @param {DateLike} date2 - The second date to compare.
 * @param {Temporal.DurationLike} offset - Optional duration offset to subtract from date2 (e.g., { days: 1 })
 * @returns {boolean} - True if date1 is before date2 (+ offset).
 */
export function isDateBeforeOrEqual(date1: DateLike, date2: DateLike, offset?: Temporal.DurationLike): boolean {
  const date2WithOffset = offset ? toPlainDate(date2).add(offset) : date2;

  return compareDates(date1, date2WithOffset) <= 0;
}

/**
 * Checks if the first date is after or equal to the second date. (compares year-month-day only, ignores time)
 * @param {DateLike} date1 - The first date to compare.
 * @param {DateLike} date2 - The second date to compare.
 * @param {Temporal.DurationLike} offset - Optional offset to add to date2 before comparison.
 * @returns {boolean} - True if date1 is after date2 (+ offset).
 */
export function isDateAfterOrEqual(date1: DateLike, date2: DateLike, offset?: Temporal.DurationLike): boolean {
  const date2WithOffset = offset ? toPlainDate(date2).add(offset) : date2;

  return compareDates(date1, date2WithOffset) >= 0;
}

/**
 * Checks if the first date is the same as the second date.
 * @param {DateLike} date1 - The first date to compare.
 * @param {DateLike} date2 - The second date to compare.
 * @returns {boolean} - True if both dates are the same.
 */
export function isSameDate(date1: DateLike, date2: DateLike): boolean {
  return compareDates(date1, date2) === 0;
}

/**
 * Returns the later of two dates.
 * @param a - First date string
 * @param b - Second date string
 * @returns The later date as an ISO date string (compares year-month-day only, ignores time)
 */
export function getLaterDate(a: string, b: string): string {
  return isDateAfter(a, b) ? toISOString(a) : toISOString(b);
}

/**
 * Add a date-level duration to a date (only supports years, months, weeks, days)
 * @param date - Base date
 * @param duration - Date-level duration (e.g., { days: 5 }, { months: 1, days: 3 })
 * @returns New date with duration added
 * @note Does NOT support time units (hours, minutes, seconds). Use addTimeDuration for time operations.
 */
export function addDateDuration(date: DateLike, duration: Temporal.DurationLike): Temporal.PlainDate {
  return toPlainDate(date).add(duration);
}

/**
 * Subtract a date-level duration from a date (only supports years, months, weeks, days)
 * @param date - Base date
 * @param duration - Date-level duration (e.g., { days: 5 }, { months: 1, days: 3 })
 * @returns New date with duration subtracted
 * @note Does NOT support time units (hours, minutes, seconds). Use subtractTimeDuration for time operations.
 */
export function subtractDateDuration(date: DateLike, duration: Temporal.DurationLike): Temporal.PlainDate {
  return toPlainDate(date).subtract(duration);
}

/**
 * Convert a date to ISO string format (YYYY-MM-DD)
 * @param {DateLike} date - Date to convert
 * @returns ISO date string
 */
export function toISOString(date: DateLike): string {
  return toPlainDate(date).toString({ calendarName: 'never' });
}
